import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';

import { Close } from '#icons/Close';
import { type Spacing } from '#themes/stena-recycling';
import { colors } from '../../../themes/stena-recycling/colors';
import { Paragraph } from '../../Typography/Paragraph';
import { Link } from '../../Navigation/Link';
import { IconButton } from '../../Input/IconButton';
import { CloseButtonWrapper, MessageBox, MessageContainer, TextWrapper } from './styles';

const mappedColorMessageTypes = (type: string) => {
  switch (type) {
    case 'success':
      return colors.alerts.successTint3;
    case 'info':
      return colors.alerts.infoTint3;
    case 'error':
      return colors.alerts.errorTint3;
    case 'warning':
      return colors.alerts.warningTint3;
    default:
      return colors.alerts.infoTint3;
  }
};

const mappedBorderMessageTypes = (type: string) => {
  switch (type) {
    case 'success':
      return colors.alerts.success;
    case 'info':
      return colors.alerts.info;
    case 'error':
      return colors.alerts.error;
    case 'warning':
      return colors.alerts.warning;
    default:
      return colors.alerts.info;
  }
};

type BaseMessageProps = {
  title?: string;
  description?: string;
  children?: React.ReactNode;
  severity?: 'success' | 'info' | 'error' | 'warning';
  show?: boolean;
  mt?: string;
  mb?: string;
  link?: {
    href: string;
    text: string;
  };

  padding?: Spacing;
  onClose?: () => void;
};

type MessageProps =
  | (BaseMessageProps & {
      closable: true;
      closedStorageKey: string;
    })
  | (BaseMessageProps & {
      closable?: false;
      closedStorageKey?: string;
    });

export const Message = ({
  title,
  description,
  link,
  severity = 'info',
  show = true,
  children,
  mb,
  mt,
  padding,
  closable,
  closedStorageKey,
  onClose,
}: MessageProps) => {
  const { t } = useTranslation();
  const [isClosed, setIsClosed] = useState(false);

  useEffect(() => {
    if (!closedStorageKey) {
      setIsClosed(false);

      return;
    }

    setIsClosed(Boolean(localStorage.getItem(closedStorageKey)));
  }, [closedStorageKey]);

  const showMessage = useMemo(() => {
    return show && !isClosed;
  }, [show, isClosed]);

  const handleClose = () => {
    if (closedStorageKey) {
      localStorage.setItem(closedStorageKey, 'true');
    }

    setIsClosed(true);
    onClose?.();
  };

  const defaultDescription =
    severity === 'error' ? t('message.components.message.error.default.text') : '';

  if (!showMessage) {
    return null;
  }

  return (
    <MessageContainer
      type={mappedColorMessageTypes(severity)}
      border={mappedBorderMessageTypes(severity)}
      mb={mb}
      mt={mt}
    >
      <MessageBox padding={padding}>
        <TextWrapper hasCloseButton={closable}>
          {title && (
            <Paragraph variant="large" bold mb="tiny">
              {title}
            </Paragraph>
          )}
          {!children && (
            <>
              <Paragraph mb={link ? 'xxsmall' : undefined} variant="large">
                {description ?? defaultDescription}
              </Paragraph>
              {link && (
                <Link inline href={link.href} color={colors.base.black} underline={true}>
                  {link.text}
                </Link>
              )}
            </>
          )}
          {children}
        </TextWrapper>
        {closable && (
          <CloseButtonWrapper>
            <IconButton onClick={handleClose} variant="secondary">
              <Close />
            </IconButton>
          </CloseButtonWrapper>
        )}
      </MessageBox>
    </MessageContainer>
  );
};

export const ErrorMessage = ({ title, description, mb, mt, link, padding }: MessageProps) => {
  return (
    <Message
      title={title}
      description={description}
      severity="error"
      mb={mb}
      mt={mt}
      link={link}
      padding={padding}
    />
  );
};
